import React from "react";
import "./issue-card-form.css";
import CustomModal from "components/custom-modal/CustomModal";
import CustomForm from "components/custom-form/CustomForm";
import Button from "components/button/Button";
import { XIconV1 } from "images/image-component";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useIssueCard } from "./useIssueCard";
import type { IssueCardStep } from "./useIssueCard";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const STEP_COPY: Record<
  IssueCardStep,
  { title: string; subtitle: string }
> = {
  1: {
    title: "Issue New Card",
    subtitle: "Enter the card number, the cardholder's email and the card's currency.",
  },
  2: {
    title: "Verify Email",
    subtitle: "Enter the code sent to the cardholder's inbox.",
  },
  3: {
    title: "Activate Card",
    subtitle: "Review the details, then activate the card.",
  },
};

function formatCountdown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function IssueCardForm({ isOpen, onClose }: Props) {
  const {
    step,
    displayStep,
    totalSteps,
    fields,
    submitRef,
    submitLabel,
    isSubmitDisabled,
    showLoading,
    loadingMsg,
    showError,
    errorMsg,
    canRetry,
    showSuccess,
    successMsg,
    noticeMsg,
    canResend,
    secondsLeft,
    handleResend,
    handleSubmit,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
    reset,
  } = useIssueCard();

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSuccessClose = () => {
    handleCloseSuccess();
    reset();
    onClose();
  };

  const { title, subtitle } = STEP_COPY[step];

  return (
    <>
      <CustomModal isOpen={isOpen} handleClose={handleClose}>
        <button
          className="v1-modal-close"
          aria-label="Close"
          onClick={handleClose}
          disabled={showLoading}
        >
          <XIconV1 />
        </button>

        <div className="icf-header">
          <span className="icf-step">
            Step {displayStep} of {totalSteps}
          </span>
          <h2 className="v1-modal-title">{title}</h2>
          <p className="v1-modal-subtitle">{subtitle}</p>
        </div>

        <CustomForm fields={fields} />

        {noticeMsg && <p className="icf-notice">{noticeMsg}</p>}

        {step === 2 && (
          <div className="icf-resend">
            {canResend ? (
              <button
                type="button"
                className="icf-resend-btn"
                onClick={handleResend}
                disabled={showLoading}
              >
                Didn't get the code? Resend
              </button>
            ) : (
              <span className="icf-resend-wait">
                You can request a new code in {formatCountdown(secondsLeft)}
              </span>
            )}
          </div>
        )}

        <div className="v1-modal-actions">
          <Button variant="ghost" onClick={handleClose} disabled={showLoading}>
            Cancel
          </Button>
          <Button
            ref={submitRef}
            onClick={handleSubmit}
            disabled={isSubmitDisabled || showLoading}
          >
            {submitLabel}
          </Button>
        </div>
      </CustomModal>

      <LoadingModal isOpen={showLoading} loadingMsg={loadingMsg} />

      <ErrorModal
        isOpen={showError}
        errorMsg={errorMsg}
        handleClose={handleCloseError}
        tryAgain={canRetry ? handleRetry : undefined}
      />

      <SuccessModal
        isOpen={showSuccess}
        message={successMsg}
        handleClose={handleSuccessClose}
      />
    </>
  );
}

export default IssueCardForm;
